import { CreateGuideStoryVariables } from 'graphql/mutation/__generated__/CreateGuideStory';
import { UpdateGuideStoryVariables } from 'graphql/mutation/__generated__/UpdateGuideStory';
import { GuideQuery_guide, GuideQuery_guide_stories } from 'graphql/query/__generated__/GuideQuery';
import { difference, omit } from 'lodash';
import { biso2024ImportMethods } from 'pages/special/ImportDetailsPage/parsers/biso2024ImportMethods';
import { bm2024ImportMethods } from 'pages/special/ImportDetailsPage/parsers/bm2024ImportMethods';
import { designFairImportMethods } from 'pages/special/ImportDetailsPage/parsers/designFairImportMethods';
import { dobrodruzstviImportMethods } from 'pages/special/ImportDetailsPage/parsers/dobrodruzstviImportMethods';
import { expoImportMethods } from 'pages/special/ImportDetailsPage/parsers/expoImportMethods';
import { iaeste2024ImportMethods } from 'pages/special/ImportDetailsPage/parsers/iaeste2024ImportMethods';
import { kdagImportMethods } from 'pages/special/ImportDetailsPage/parsers/kdagImportMethods';
import { liege2024ImportMethods } from 'pages/special/ImportDetailsPage/parsers/liege2024ImportMethods';
import { liegeImportMethods } from 'pages/special/ImportDetailsPage/parsers/liegeImportMethods';
import { osuImportMethods } from 'pages/special/ImportDetailsPage/parsers/osuImportMethods';
import { ovOlomouc2024ProgramImportMethods } from 'pages/special/ImportDetailsPage/parsers/ovOlomouc2024ProgramImportMethods';
import { seafoodCompaniesImportMethods } from 'pages/special/ImportDetailsPage/parsers/seafoodCompaniesImportMethods';
import { seafoodProgrammeImportMethods } from 'pages/special/ImportDetailsPage/parsers/seafoodProgrammeImportMethods';
import { utrechtImportMethods } from 'pages/special/ImportDetailsPage/parsers/utrechtImportMethods';
import { vsbImportMethods } from 'pages/special/ImportDetailsPage/parsers/vsbImportMethods';
import { vschtImportMethods } from 'pages/special/ImportDetailsPage/parsers/vschtImportMethods';
import { vutImportMethods } from 'pages/special/ImportDetailsPage/parsers/vutImportMethods';

export type ImportDetailParser = (
    guideId: string,
    row: Record<string, string>
) => CreateGuideStoryVariables;

export interface ImportOperationsResult {
    creates: CreateGuideStoryVariables[];
    updates: UpdateGuideStoryVariables[];
    unlinks?: GuideQuery_guide_stories[];
}

export type DuplicateChecker = (
    story: GuideQuery_guide_stories,
    row: Record<string, string>
) => boolean;

export interface ImportMethods {
    parser: ImportDetailParser;
    duplicateChecker: DuplicateChecker;
    importKeyColumn?: string;
}

export const IMPORT_DETAIL_PARSERS: Record<
    string,
    { parser: ImportDetailParser; duplicateChecker: DuplicateChecker; importKeyColumn?: string }
> = {
    vsb: vsbImportMethods,
    utrecht: utrechtImportMethods,
    seafoodCompanies: seafoodCompaniesImportMethods,
    seafoodProgramme: seafoodProgrammeImportMethods,
    liege: liegeImportMethods,
    kdag: kdagImportMethods,
    vscht: vschtImportMethods,
    vut: vutImportMethods,
    osu: osuImportMethods,
    dobrodruzstvi: dobrodruzstviImportMethods,
    expo: expoImportMethods,
    biso2024: biso2024ImportMethods,
    liege2024: liege2024ImportMethods,
    ovOlomoucProgram2024: ovOlomouc2024ProgramImportMethods,
    bm2024: bm2024ImportMethods,
    iaeste2024: iaeste2024ImportMethods,
    designFair: designFairImportMethods
};

const getImportOperations = (
    guide: GuideQuery_guide,
    rows: Record<string, string>[],
    importRowKey: string | undefined,
    selectedListId: string | null,
    parser: ImportDetailParser,
    duplicateChecker: DuplicateChecker
) => {
    const creates: CreateGuideStoryVariables[] = [];
    const updates: UpdateGuideStoryVariables[] = [];
    const unlinks: GuideQuery_guide_stories[] = [];

    for (const row of rows) {
        const storiesToSearch = guide.stories.filter(
            (story) => !updates.some((update) => update.storyId === story.storyId)
        );

        const matchStory = storiesToSearch.find((story) => duplicateChecker(story, row));

        const payload = parser(guide.guideId, row);

        if (matchStory) {
            updates.push({
                ...omit(payload, ['assignToEntityTypeId']),
                storyId: matchStory.storyId
            });
        } else {
            creates.push(payload);
        }
    }

    if (selectedListId) {
        const currentStoryListIds =
            guide.entityTypes.find((e) => e.guideEntityTypeId === selectedListId).storyIds ?? [];
        const currentStoryImportIds = currentStoryListIds
            .map((e) => guide.stories.find((s) => s.storyId === e).importKey)
            ?.filter(Boolean);
        const rowImportIds = rows.map((row) => row[importRowKey]);
        const unlinkImportIds = difference(currentStoryImportIds, rowImportIds);
        const unlinkStories = unlinkImportIds.map((e) =>
            guide.stories.find((s) => s.importKey === e)
        );

        unlinks.push(...unlinkStories);
    }

    return {
        creates,
        updates,
        unlinks
    };
};

export function prepareImportPayloadData(
    parserName: string,
    guide: GuideQuery_guide,
    rows: Record<string, string>[],
    assignListId: string | null
): ImportOperationsResult {
    const methods = IMPORT_DETAIL_PARSERS[parserName];
    return getImportOperations(
        guide,
        rows,
        methods.importKeyColumn,
        assignListId,
        methods.parser,
        methods.duplicateChecker
    );
}
